/**
 * FocusWalkGate — the morning lockout for AlderFLOW's Focus walk (docs/ALDERFLOW.md §5c). Shown only to
 * whoever holds the Operations Director role (Matt, 2026-10-22: "go by role"), once a day for the whole
 * firm, while the engine has cards; it closes for everyone on Commit or "Not today". The server decides (api/alderflow.js
 * `gate`); this fails OPEN on any error, like every other lockout (docs/LOCKOUTS.md).
 *
 * Waits for the Time Gate, the Captain's report and Triage to be settled and clear, so lockouts never
 * stack. Never in an embedded split pane. Mounted once in OutlineView, after TriageGate.
 */
import RoleChip from './RoleChip.jsx'
import { useEffect, useState, useCallback, useRef } from 'react'
import { ctx } from '../outline/ctx.js'
import { isSeatLoaded } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { lockScroll } from '../outline/scrollLock.js'
import { getFocusWalkGate, snoozeFocusWalk } from '../services/alderflow.js'
import FocusWalk from './FocusWalk.jsx'

export default function FocusWalkGate() {
  const [walk, setWalk] = useState(null)
  const [gated, setGated] = useState(false)
  const [timeBlocking, setTimeBlocking] = useState(null)
  const [reportBlocking, setReportBlocking] = useState(null)
  const [triageBlocking, setTriageBlocking] = useState(null)
  const checked = useRef(false)
  const [decided, setDecided] = useState(false)

  useEffect(() => {
    const t = (e) => setTimeBlocking(!!(e.detail && e.detail.blocking))
    const r = (e) => setReportBlocking(!!(e.detail && e.detail.blocking))
    const g = (e) => setTriageBlocking(!!(e.detail && e.detail.blocking))
    window.addEventListener('time-gate-blocking-changed', t)
    window.addEventListener('report-gate-blocking-changed', r)
    window.addEventListener('triage-gate-blocking-changed', g)
    return () => { window.removeEventListener('time-gate-blocking-changed', t); window.removeEventListener('report-gate-blocking-changed', r); window.removeEventListener('triage-gate-blocking-changed', g) }
  }, [])

  const who = () => {
    const m = ctx.currentMember
    return { id: m && m.id, name: m && m.fields ? (m.fields['Member Name'] || m.fields['Name'] || null) : null }
  }
  const check = useCallback(async () => {
    const { id, name } = who()
    if (!id || !isSeatLoaded()) return   // the server decides who walks it: the Operations Director role
    try {
      const r = await getFocusWalkGate(id, name)
      setGated(!!(r && r.gated)); setWalk(r && r.walk ? r.walk : null)
    } catch { setGated(false) }   // fail open
    setDecided(true)
  }, [])

  // The other gates settle first; then check once (and again when the window regains focus).
  const othersClear = timeBlocking === false && reportBlocking !== true && triageBlocking === false
  useEffect(() => {
    if (!othersClear || checked.current) return
    let tries = 0
    const iv = setInterval(() => {
      tries++
      if (isSeatLoaded() && ctx.currentMember && ctx.currentMember.id) { clearInterval(iv); checked.current = true; check() }
      else if (tries > 40) clearInterval(iv)
    }, 400)
    return () => clearInterval(iv)
  }, [othersClear, check])

  const blocking = !isEmbedded() && gated && othersClear && !!walk
  useEffect(() => { if (blocking) return lockScroll() }, [blocking])
  // Tell anything after this one once it has been decided, and when it closes.
  useEffect(() => { if (decided) window.dispatchEvent(new CustomEvent('focus-walk-blocking-changed', { detail: { blocking } })) }, [decided, blocking])
  if (!blocking) return null

  async function notToday() {
    const { id, name } = who()
    try { await snoozeFocusWalk(id, name) } catch { /* the gate fails open anyway */ }
    setGated(false)
  }

  return (
    <div style={S.overlay} role="dialog" aria-modal="true" aria-label="Operations Director Triage">
      <div style={S.card}>
        <RoleChip role="Operations Director" what="The firm's plan: what needs your call today" />
        <div style={S.eyebrow}>{(walk && walk.title) || 'Operations Director Triage'}</div>
        <h2 style={S.title}>{(walk && walk.cards ? walk.cards.length : 0) === 1 ? 'One thing in the plan needs your call today' : `${(walk && walk.cards ? walk.cards.length : 0)} things in the plan need your call today`}</h2>
        <hr style={S.rule} />
        <p style={S.lead}>Pick an answer on each card. Nothing changes until you press Commit at the end.</p>
        <div style={S.scroll}>
          <FocusWalk walk={walk} onDone={() => setGated(false)} />
        </div>
        <div style={{ textAlign: 'center', marginTop: 14, flexShrink: 0 }}>
          <button style={S.link} onClick={notToday}>Not today (it comes back tomorrow morning)</button>
        </div>
      </div>
    </div>
  )
}

// The same blurred-scrim card as the daily triage (TriageGate.jsx), Matt 2026-10-22: it opened as a
// bare white page.
const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 1000000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(4,21,24,0.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' },
  card: { width: '100%', maxWidth: 720, maxHeight: 'calc(100vh - 48px)', background: 'rgba(255,255,255,0.97)', border: '0.5px solid rgba(255,255,255,0.6)', borderRadius: 16, boxShadow: '0 24px 64px rgba(4,21,24,0.5)', padding: 'clamp(24px, 3.5vw, 40px)', display: 'flex', flexDirection: 'column', minHeight: 0 },
  eyebrow: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#b8540a', marginBottom: 12 },
  title: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(17px, 2.2vw, 22px)', fontWeight: 300, letterSpacing: '0.03em', color: '#07272D', lineHeight: 1.3, margin: 0 },
  rule: { flexShrink: 0, height: '0.5px', background: 'rgba(7,39,45,0.12)', border: 'none', margin: '16px 0', width: 60 },
  lead: { flexShrink: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, color: '#3a5260', margin: '0 0 16px', lineHeight: 1.55 },
  scroll: { overflowY: 'auto', flex: '1 1 auto', minHeight: 0, marginRight: -8, paddingRight: 8 },
  link: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', background: 'none', border: 0, cursor: 'pointer', textDecoration: 'underline' },
}
